import React, { useState, useEffect } from "react";

import {
   PaginationProps,
   calculatePagesQuantity,
   createPageButton,
   getButtonsRange,
   checkIfRightButtonsShouldRender,
   checkInitialPageLimit,
} from "./PaginationScripts";

import "./Pagination.less";

//TODO: in free time :D
// write tests
// check jsdocs, many of them where generated by chatgpt :D

/**
 * A pagination component.
 *
 * Renders a pagination control for navigating through pages.
 *
 * @param {PaginationProps} props -    initialPage | defaultPageSize | totalItems | customClassName? | customIcon? | onChange.
 * @returns {JSX.Element} - The rendered component.
 */

export const Pagination: React.FC<PaginationProps> = ({
   initialPage,
   defaultPageSize,
   totalItems,
   customClassName,
   customIcon,
   onChange,
}) => {
   let pagesQuantity = calculatePagesQuantity(totalItems, defaultPageSize);

   const [currentPage, setCurrentPage] = useState<number>(
      checkInitialPageLimit(initialPage, pagesQuantity),
   );

   const [showRightSkipButton, setShowRightSkipButton] = useState(false);
   const [showLeftSkipButton, setShowLeftSkipButton] = useState(false);

   // we need to make array, based on page quantity (to iterate something). "index + 1" is for starting indexing pages from 1 and not 0
   const pages = Array.from({ length: pagesQuantity }, (ignoredParam, index) => index + 1);

   // when changeing items per page (defaultPageSize), there could be situation that current page will be grater then pagesQuantity. If so, we skip to first page.
   useEffect(() => {
      if (initialPage > pagesQuantity) {
         handlePageChange(pagesQuantity);
      }
   }, [defaultPageSize]);

   useEffect(() => {
      if (initialPage) {
         const page = checkInitialPageLimit(initialPage, pagesQuantity);
         setCurrentPage(page);
      }
   }, [initialPage, pagesQuantity]);

   const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
      onChange(pageNumber);
   };

   // those to are for changing styles of icons (skip 5 pages)
   const handleMouseEnter = (iconDirection) => {
      if (iconDirection === "right") {
         setShowRightSkipButton(true);
      } else {
         setShowLeftSkipButton(true);
      }
   };
   const handleMouseLeave = (iconDirection) => {
      if (iconDirection === "right") {
         setShowRightSkipButton(false);
      } else {
         setShowLeftSkipButton(false);
      }
   };

   const endPageButton = (pagesQuantity) => {
      if (pagesQuantity > 5 && !getButtonsRange(currentPage, pages)?.includes(pagesQuantity)) {
         return createPageButton(pagesQuantity, handlePageChange, currentPage);
      }
   };

   const firstPageButton = () => {
      if (!getButtonsRange(currentPage, pages)?.includes(1)) {
         return createPageButton(1, handlePageChange, currentPage);
      }
   };

   const renderMiddleButtons = () => {
      if (totalItems && defaultPageSize) {
         // iterating single "square" page button
         return getButtonsRange(currentPage, pages)?.map((pageNumber) =>
            createPageButton(pageNumber, handlePageChange, currentPage),
         );
      }
      return null;
   };

   return (
      <div className={customClassName !== undefined ? customClassName : "main-container"}>
         <button
            //  1 becouse we move by one page
            onClick={() => (currentPage > 1 ? handlePageChange(currentPage - 1) : null)}
            className="next-page-pagination-buttons"
         >
            {customIcon ? (
               customIcon
            ) : (
               <span className="material-symbols-outlined">chevron_left</span>
            )}
         </button>

         {firstPageButton()}

         {Math.max(...getButtonsRange(currentPage, pages)) > 6 ? (
            showLeftSkipButton ? (
               <div
                  onClick={() => handlePageChange(currentPage - 5)}
                  className="pagination-divider"
                  onMouseEnter={() => handleMouseEnter("left")}
                  onMouseLeave={() => handleMouseLeave("left")}
               >
                  <span className="material-symbols-outlined">keyboard_double_arrow_left</span>
               </div>
            ) : (
               <div
                  className="pagination-divider"
                  onMouseEnter={() => handleMouseEnter("left")}
                  onMouseLeave={() => handleMouseLeave("left")}
               >
                  <span className="material-symbols-outlined">more_horiz</span>
               </div>
            )
         ) : null}

         {renderMiddleButtons()}

         {checkIfRightButtonsShouldRender(pagesQuantity, getButtonsRange(currentPage, pages)) ? (
            showRightSkipButton ? (
               <div
                  onClick={() => handlePageChange(currentPage + 5)}
                  className="pagination-divider"
                  onMouseEnter={() => handleMouseEnter("right")}
                  onMouseLeave={() => handleMouseLeave("right")}
               >
                  <span className="material-symbols-outlined">double_arrow</span>
               </div>
            ) : (
               <div
                  className="pagination-divider"
                  onMouseEnter={() => handleMouseEnter("right")}
                  onMouseLeave={() => handleMouseLeave("right")}
               >
                  <span className="material-symbols-outlined">more_horiz</span>
               </div>
            )
         ) : null}

         {endPageButton(pagesQuantity)}

         <button
            //  1 becouse we move by one page
            onClick={() => (pagesQuantity > currentPage ? handlePageChange(currentPage + 1) : null)}
            className="next-page-pagination-buttons"
         >
            {customIcon ? (
               customIcon
            ) : (
               <span className="material-symbols-outlined">chevron_right</span>
            )}
         </button>
      </div>
   );
};

export default Pagination;
